import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { calculateInvoiceTotals } from '../utils/helpers';
import Alert from '../components/Alert';
import InvoiceItemsTable from '../components/InvoiceItemsTable';
import CustomerModal from '../components/CustomerModal';
import Layout from '../components/Layout';
import AutocompleteSelect from '../components/AutocompleteSelect';
import CustomDropdown from '../components/CustomDropdown';
import { 
  PlusCircle, 
  User, 
  Package, 
  FileText, 
  Calculator, 
 
  ArrowLeft, 
  UserPlus,

  Loader2,

  Lightbulb,
  Phone,
  Mail,
  Plus
} from 'lucide-react';

const CreateInvoice = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);

  const [formData, setFormData] = useState({
    customer: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    items: [{
      product: '',
      description: '',
      hsnSac: '',
      quantity: 1,
      unit: 'pcs',
      rate: 0,
      gstRate: 18
    }],
    discount: 0,
    discountType: 'amount',
    taxType: 'excluded',
    notes: '',
    termsAndConditions: '',
    sendEmail: false
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [settingsRes, customersRes, productsRes] = await Promise.all([
        api.get('/settings'),
        api.get('/customers?limit=1000'),
        api.get('/products?limit=1000')
      ]);

      setSettings(settingsRes.data.settings);
      setCustomers(customersRes.data.customers);
      setProducts(productsRes.data.products);

      if (settingsRes.data.settings) {
        setFormData(prev => ({
          ...prev,
          taxType: settingsRes.data.settings.defaultTaxType || 'excluded',
          termsAndConditions: settingsRes.data.settings.termsAndConditions || prev.termsAndConditions
        }));
      }
    } catch (err) {
      setError('Failed to load initial data');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleCustomerCreate = async (customerData) => {
    try {
      const response = await api.post('/customers', customerData);
      const newCustomer = response.data.customer;

      setCustomers(prev => [...prev, newCustomer]);
      setFormData(prev => ({ ...prev, customer: newCustomer._id }));
      setSuccess('Customer added successfully!');
      setShowCustomerModal(false);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create customer');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customer) {
      setError('Please select a customer');
      return;
    }

    if (formData.items.length === 0) {
      setError('Please add at least one item');
      return;
    }

    const hasEmptyItems = formData.items.some(
      item => (!item.productName && !item.description) || !item.hsnSac || Number(item.quantity) <= 0 || Number(item.rate) < 0
    );
    if (hasEmptyItems) {
      setError('Please fill in all item details (product name/description, HSN/SAC, quantity, and rate)');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Sanitize items array to remove empty product fields and ensure description is filled
      const sanitizedData = {
        ...formData,
        items: formData.items.map(item => {
          const sanitizedItem = { ...item };
          sanitizedItem.description = (sanitizedItem.description || sanitizedItem.productName || '').trim();
          sanitizedItem.productName = (sanitizedItem.productName || '').trim();
          sanitizedItem.unit = (sanitizedItem.unit || 'pcs').trim();
          if (!sanitizedItem.product) {
            delete sanitizedItem.product;
          }
          return sanitizedItem;
        })
      };

      const response = await api.post('/invoices', sanitizedData);

      if (response.data.success) {
        setSuccess('Invoice created successfully');
        navigate(`/invoice/${response.data.invoice._id}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create invoice');
      setLoading(false);
    }
  };

  const selectedCustomer = customers.find(c => c._id === formData.customer);
  const isIGST = settings && selectedCustomer ? selectedCustomer.placeOfSupply !== settings.state : false;
  const totals = calculateInvoiceTotals(formData.items, isIGST, formData.discount, formData.discountType, formData.taxType);

  return (
    <div>
      <div className="create-invoice-page">
        {/* Page Header */}
        <div className="page-header" style={{ marginBottom: '30px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <PlusCircle size={32} style={{ color: '#3b82f6' }} />
            <div>
              <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '5px', margin: 0 }}>
                Create Invoice
              </h1>
              <p style={{ color: '#666', fontSize: '15px', margin: 0 }}>
                Create a new invoice for your customer
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/invoice')}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <ArrowLeft size={18} /> Back to Invoices
          </button>
        </div>

        {error && <Alert type="error" message={error} onClose={() => setError('')} />}
        {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

        <form onSubmit={handleSubmit}>
          {/* Customer Selection Card */}
          <div className="card" style={{ marginBottom: '25px', overflow: 'visible', position: 'relative', zIndex: 10 }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb' }}>
              <h2 style={{
                fontSize: '17px',
                fontWeight: '600',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <User size={18} style={{ color: '#3b82f6' }} /> Customer Information
              </h2>
            </div>

            <div style={{ padding: '25px', overflow: 'visible' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 150px 200px',
                gap: '20px',
                alignItems: 'flex-start',
                overflow: 'visible'
              }}>
                <div style={{ position: 'relative', overflow: 'visible' }}>
                  <label className="form-label" style={{ fontWeight: '500' }}>
                    Select Customer <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <AutocompleteSelect
                    options={customers}
                    value={formData.customer}
                    onChange={(value) => setFormData(prev => ({ ...prev, customer: value }))}
                    displayField="name"
                    valueField="_id"
                    placeholder="Type to search customer by name, phone, or Product ID..."
                    searchFields={['name', 'phone', 'email']}
                    className="form-control"
                    required
                    renderOption={(customer) => (
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#111' }}>
                          {customer.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Phone size={12} /> {customer.phone} {customer.email && <><Mail size={12} style={{ marginLeft: '4px' }} /> {customer.email}</>}
                        </div>
                      </div>
                    )}
                  />
                </div>

                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => setShowCustomerModal(true)}
                  style={{
                    marginTop: '28px',
                    padding: '10px 18px',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <UserPlus size={18} /> New Customer
                </button>


                
                <div>
                  <label className="form-label" style={{ fontWeight: '500' }}>
                    Invoice Date <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="date"
                    name="invoiceDate"
                    value={formData.invoiceDate}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>
              </div>

              {selectedCustomer && (
                <div style={{
                  marginTop: '20px',
                  padding: '15px',

                  borderLeft: '4px solid #3b82f6',
                  borderRadius: '6px'
                }}>
                  <div style={{ fontSize: '14px' }}>
                    <strong style={{ color: '#1e40af' }}>Selected Customer:</strong>
                  </div>
                  <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', fontSize: '13px' }}>
                    <div>
                      <strong>Name:</strong> {selectedCustomer.name}
                    </div>
                    <div>
                      <strong>Phone:</strong> {selectedCustomer.phone}
                    </div>
                    {selectedCustomer.email && (
                      <div>
                        <strong>Email:</strong> {selectedCustomer.email}
                      </div>
                    )}
                    {selectedCustomer.city && (
                      <div>
                        <strong>City:</strong> {selectedCustomer.city}
                      </div>
                    )}
                    {selectedCustomer.gstNumber && (
                      <div>
                        <strong>GST:</strong> {selectedCustomer.gstNumber}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Invoice Items Card */}
          <div className="card" style={{ marginBottom: '25px', overflow: 'visible', position: 'relative', zIndex: 9 }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb' }}>
              <h2 style={{
                fontSize: '17px',
                fontWeight: '600',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Package size={18} style={{ color: '#3b82f6' }} /> Invoice Items
              </h2>
            </div>

            <div style={{ padding: '25px', overflow: 'visible' }}>
              <InvoiceItemsTable
                items={formData.items}
                products={products}
                onItemsChange={(items) => setFormData(prev => ({ ...prev, items }))}
              />
            </div>
          </div>

          {/* Payment & Discount Section */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px', marginBottom: '25px' }}>
            {/* Notes Section */}
            <div className="card">
              <div style={{
                padding: '20px',
                borderBottom: '1px solid #e5e7eb' }}>
                <h2 style={{ fontSize: '17px', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FileText size={18} style={{ color: '#3b82f6' }} /> Additional Information
                </h2>
              </div>

              <div style={{ padding: '25px' }}>
                <div>
                  <label className="form-label">Notes (Optional)</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    className="form-control"
                    rows="6"
                    placeholder="Add special instructions or notes..."
                  />
                  <p style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Lightbulb size={14} /> Payment details can be added after invoice creation
                  </p>
                </div>
              </div>
            </div>

            {/* Totals & Discount */}
            <div className="card" style={{ overflow: 'visible', position: 'relative', zIndex: 8 }}>
              <div style={{
                padding: '20px',
                borderBottom: '1px solid #e5e7eb' }}>
                <h2 style={{ fontSize: '17px', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Calculator size={18} style={{ color: '#3b82f6' }} /> Invoice Summary
                </h2>
              </div>

              <div style={{ padding: '25px' }}>
                {/* Discount Section */}
                <div style={{ marginBottom: '25px', padding: '15px', borderRadius: '8px' }}>
                  <label className="form-label" style={{ marginBottom: '12px', fontWeight: '600' }}>
                    Apply Discount
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: '10px' }}>
                    <input
                      type="number"
                      name="discount"
                      value={formData.discount}
                      onChange={handleChange}
                      className="form-control"
                      min="0"
                      step="0.01"
                      placeholder="Enter discount"
                    />
                    <CustomDropdown
                      name="discountType"
                      value={formData.discountType}
                      onChange={handleChange}
                      options={[
                        { value: 'amount', label: '₹ Amount' },
                        { value: 'percentage', label: '% Percent' }
                      ]}
                      className="form-control"
                      style={{ height: '42px', minWidth: '130px' }}
                    />
                  </div>
                </div>

                {/* Totals Summary */}
                <div style={{ fontSize: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px dashed #e0e0e0' }}>
                    <span style={{ color: '#666' }}>Subtotal</span>
                    <span style={{ fontWeight: '600' }}>₹{totals.subtotal.toFixed(2)}</span>
                  </div>

                  {formData.discount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px dashed #e0e0e0', color: '#dc2626' }}>
                      <span>Discount</span>
                      <span style={{ fontWeight: '600' }}>-₹{totals.discountAmount.toFixed(2)}</span>
                    </div>
                  )}

                  {isIGST ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px dashed #e0e0e0' }}>
                      <span style={{ color: '#666' }}>IGST</span>
                      <span style={{ fontWeight: '600' }}>₹{totals.igst.toFixed(2)}</span>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px dashed #e0e0e0' }}>
                        <span style={{ color: '#666' }}>CGST</span>
                        <span style={{ fontWeight: '600' }}>₹{totals.cgst.toFixed(2)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px dashed #e0e0e0' }}>
                        <span style={{ color: '#666' }}>SGST</span>
                        <span style={{ fontWeight: '600' }}>₹{totals.sgst.toFixed(2)}</span>
                      </div>
                    </>
                  )}

                  <div style={{ borderBottom: '2px solid #333', marginBottom: '15px', paddingBottom: '10px' }}></div>

                  {/* Grand Total */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '18px 20px',

                    background: 'var(--accent)',
                    borderRadius: '10px',
                    color: 'white',
                    fontSize: '18px'
                  }}>
                    <span style={{ fontWeight: '700' }}>GRAND TOTAL</span>
                    <span style={{ fontWeight: '700', fontSize: '22px' }}>₹{totals.grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '15px',
            justifyContent: 'flex-end',
            padding: '20px 0'
          }}>
            <button
              type="button"
              onClick={() => navigate('/invoice')}
              className="btn btn-secondary"
              style={{ padding: '12px 35px', fontSize: '15px' }}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
              style={{
                padding: '12px 45px',
                fontSize: '15px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                border: 'none',
                boxShadow: loading ? 'none' : '0 4px 15px rgba(102, 126, 234, 0.4)'
              }}
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
              {loading ? 'Creating Invoice...' : 'Create Invoice'}
            </button>
          </div>
        </form>

        {/* Customer Modal */}
        {showCustomerModal && (
          <CustomerModal
            isOpen={showCustomerModal}
            onClose={() => setShowCustomerModal(false)}
            onSubmit={handleCustomerCreate}
          />
        )}
      </div>
    </div>
  );
};

export default CreateInvoice;
